import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  ListItemText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Badge,
  Autocomplete,
  Chip,
  TextField,
  Button,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MenuIcon from '@mui/icons-material/Menu';
import { ReactComponent as MoreVertIcon } from '../../../../assets/common-assets/moreVertical.svg';
import { lightPalette } from '../../../../theme';
import {
  deleteAllDocuments,
  deleteSelectedDocuments,
  getStage,
  gridFilterSortStatus,
  updateColumnHideStatus,
} from '../../../../services/allInvoiceApi';
import {
  CurserPointerBox,
  SelectColumnMenu,
  classes,
  AutoCompleteRenderListTypography,
  PaperComponentBox,
} from './utils';
import CustomTooltip from '../../utils/CustomTooltip';
import ConfirmDialog from '../../utils/ConfirmDialog';
import OnselectAddTag from '../../document-management/documents/OnselectAddTag';
import ExportDocument from '../../document-management/documents/ExportDocument';
import trash from '../../../../assets/allInvoice-assets/trash-2.svg';
import Toast from '../../utils/Toast';
import { DOCUMENT_ACTION_TOOLTIP } from '../../../../constants/tooltipConstant';
import { DOCUMENT_TOAST } from '../../../../constants/toastConstant';
import filter from '../../../../assets/paymenthistory-assets/filter.svg';
import { globalClasses } from '../../utils/globalUtils';
import {
  markAllAsReadNotifications,
  markAsReadForSelectedAsNotifications,
} from '../../../../services/tabSection';
import { getTagData } from '../../../../services/tagApi';
import { getCountries } from '../../../../services/addMemberApi';
import { FILTER } from '../../../../constants/staticContent';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import ROLES from '../../../../constants/roles';
import MODELS from '../../../../constants/models';

export const ColumnSelection = ({
  columnDataForFilter,
  columnApi,
  gridSettingData,
  gridKey,
  gridApi,
  customFilterValue,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState({});
  const [isColumnUpdated, setIsColumnUpdated] = useState(false);

  const filterModel = gridApi?.getFilterModel();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCheckboxChange = (columnName, isChecked) => {
    setIsColumnUpdated(true);
    setSelectedColumns((prevSelectedColumns) => ({
      ...prevSelectedColumns,
      [columnName]: isChecked,
    }));
  };

  const updateSelectedColumn = async (gridName, updatedColumn) => {
    try {
      const response = await updateColumnHideStatus(gridName, updatedColumn);
      if (response) {
        setIsColumnUpdated(false);
      }
    } catch (err) {
      setIsColumnUpdated(false);
      return err;
    }
  };

  useEffect(() => {
    if (
      gridSettingData?.settings &&
      Object.keys(gridSettingData.settings).length > 0
    ) {
      setSelectedColumns(gridSettingData?.settings);
    } else {
      setSelectedColumns(
        Object.fromEntries(
          columnDataForFilter.map((column) => [column.field, true]),
        ),
      );
    }
  }, [gridSettingData]);

  useEffect(() => {
    if (isColumnUpdated) {
      updateSelectedColumn(gridKey, selectedColumns);
    }
  }, [isColumnUpdated]);

  useEffect(() => {
    Object.entries(selectedColumns).forEach(([colName, isVisible]) => {
      columnApi?.setColumnVisible(colName, isVisible);
    });
  }, [selectedColumns]);

  function checkCondition(customFilterValues, columnDefs) {
    if (!customFilterValues || !columnDefs || !Array.isArray(columnDefs)) {
      return false;
    }

    let conditionMet = false;

    Object.keys(customFilterValues).forEach((key) => {
      const hasMatchingField = columnDefs.some(
        (columnDef) => columnDef.field === key,
      );
      if (hasMatchingField && customFilterValues[key].length > 0) {
        conditionMet = true;
      }
    });

    return conditionMet;
  }

  function checkColumName(customFilterValues, columnDefs) {
    if (!customFilterValues || !columnDefs || !Array.isArray(columnDefs)) {
      return null; // or any default value you prefer
    }

    let columnName = null;

    Object.keys(customFilterValues).some((key) => {
      const hasMatchingField = columnDefs.some(
        (columnDef) =>
          columnDef.field === key &&
          customFilterValues[columnDef.field].length > 0,
      );

      if (hasMatchingField && customFilterValues[key].length > 0) {
        columnName = key;
        return true; // Exit the loop once a matching column is found
      }
      return false;
    });

    return columnName;
  }

  const conditionResult = checkCondition(
    customFilterValue,
    columnApi?.columnModel?.columnDefs,
  );
  const columName = checkColumName(
    customFilterValue,
    columnApi?.columnModel?.columnDefs,
  );

  const customFilterArray =
    customFilterValue &&
    Object.values(customFilterValue)?.filter((f) => f.length > 0);

  const getFilterIcon = () => {
    let tooltipTitle;
    let shouldDisplayTooltip = false;

    if (filterModel && Object.keys(filterModel).length > 0) {
      shouldDisplayTooltip = true;
      tooltipTitle =
        Object.keys(filterModel).length > 1
          ? `filter is applied on ${Object.keys(filterModel).length} column`
          : `filter is applied on ${Object.keys(filterModel)[0]} column`;
    } else if (conditionResult) {
      shouldDisplayTooltip = true;
      tooltipTitle =
        customFilterArray && customFilterArray.length > 1
          ? `filter is applied on ${customFilterArray.length} column`
          : `filter is applied on ${columName} column`;
    }

    return shouldDisplayTooltip ? (
      <CustomTooltip title={tooltipTitle}>
        <Badge
          color="secondary"
          badgeContent=" "
          variant="dot"
          className={classes.moreVertIconBadge}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <CurserPointerBox>
            <MoreVertIcon onClick={handleClick} />
          </CurserPointerBox>
        </Badge>
      </CustomTooltip>
    ) : (
      <CurserPointerBox>
        <MoreVertIcon onClick={handleClick} />
      </CurserPointerBox>
    );
  };

  return (
    <Box>
      {getFilterIcon()}
      <SelectColumnMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {columnDataForFilter &&
          columnDataForFilter.length > 0 &&
          columnDataForFilter.map((column) =>
            column.field !== ' ' && column.hide !== false ? (
              <MenuItem key={column.field}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedColumns[column.field] || false}
                      onChange={(e) =>
                        handleCheckboxChange(column.field, e.target.checked)
                      }
                    />
                  }
                  label={
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {column.headerName}
                        </Typography>
                      }
                    />
                  }
                />
              </MenuItem>
            ) : null,
          )}
      </SelectColumnMenu>
    </Box>
  );
};

ColumnSelection.defaultProps = {
  columnApi: null,
  gridApi: null,
  gridSettingData: null,
};

ColumnSelection.propTypes = {
  columnDataForFilter: PropTypes.arrayOf(
    PropTypes.shape({
      checkboxSelection: PropTypes.bool,
      field: PropTypes.string,
      headerComponent: PropTypes.string,
      headerName: PropTypes.string,
      maxWidth: PropTypes.number,
      minWidth: PropTypes.number,
      resizable: PropTypes.bool,
      sortable: PropTypes.bool,
    }),
  ).isRequired,
  columnApi: PropTypes.shape({
    setColumnVisible: PropTypes.func.isRequired,
    columnModel: PropTypes.func.isRequired,
  }),
  gridApi: PropTypes.shape({
    getFilterModel: PropTypes.func.isRequired,
  }),
  gridSettingData: PropTypes.shape({
    id: PropTypes.string,
    user_id: PropTypes.string,
    grid_key: PropTypes.string,
    settings: PropTypes.shape({
      ' ': PropTypes.bool.isRequired,
      name: PropTypes.bool.isRequired,
      status: PropTypes.bool.isRequired,
      associatedTags: PropTypes.bool.isRequired,
      uploaded: PropTypes.bool.isRequired,
      versions: PropTypes.bool.isRequired,
    }),
    sort: PropTypes.shape({
      column: PropTypes.string,
      sort: PropTypes.string,
    }),
    filter: PropTypes.shape({}),
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }),
  gridKey: PropTypes.string.isRequired,
  customFilterValue: PropTypes.shape({}).isRequired,
};

export const ResponsiveSortingHeader = memo(
  function ResponsiveSortingHeader(props) {
    const { gridSettingData, gridKey, customFilterValue } = props;
    const [sortState, setSortState] = useState();
    const [displayName, setDisplayName] = useState('');
    const refButton = useRef(null);

    const filterModel = props?.api?.getFilterModel();

    const onClick = useCallback(() => {
      if (props?.column?.colDef?.sortable) {
        props.progressSort();
      }
    });

    useEffect(() => {
      const listener = () => {
        if (sortState !== 'ASC' && props.column.isSortAscending()) {
          setSortState('ASC');
        } else if (props.column.isSortDescending()) {
          setSortState('DESC');
        } else {
          setSortState(undefined);
        }
      };
      props.column.addEventListener('sortChanged', listener);

      return () => props.column.removeEventListener('sortChanged', listener);
    }, []);

    useEffect(() => {
      if (gridKey) {
        if (gridSettingData && Object.keys(gridSettingData).length > 0) {
          if (gridSettingData?.sort?.column === props.column.colId) {
            setSortState(
              gridSettingData?.sort?.sort === 'asc' ? 'ASC' : 'DESC',
            );
            setDisplayName(props.displayName);
          } else {
            setDisplayName(props.displayName);
          }
        } else {
          setDisplayName(props.displayName);
        }
      }
    }, [gridKey]);

    let icon;
    if (sortState === 'ASC') {
      icon = (
        <span>
          <i className="fa fa-arrow-up" style={{ marginLeft: '8px' }} />
        </span>
      );
    } else if (sortState === 'DESC') {
      icon = (
        <span>
          <i className="fa fa-arrow-down" style={{ marginLeft: '8px' }} />
        </span>
      );
    }

    const handleFilterClick = () => {
      props.showColumnMenu(refButton.current);
    };

    let menu = null;
    if (props.enableMenu) {
      menu = (
        <Box
          ref={refButton}
          onClick={() => handleFilterClick()}
          className={`${globalClasses.displayFlex} ${classes.justifyContentFlexEnd}`}
        >
          <MenuIcon className={classes.menuIconColor} />
        </Box>
      );
    }

    const customFilterColumn =
      customFilterValue &&
      props?.column?.colDef?.field &&
      props.column.colDef.field in customFilterValue &&
      Array.isArray(customFilterValue[props.column.colDef.field]) &&
      customFilterValue[props.column.colDef.field].length > 0;

    const filterClassName = (filterModels, prop, filterColumn) => {
      let className = globalClasses.notVisibility;

      if (
        (filterModels &&
          Object.keys(filterModels).length > 0 &&
          Object.keys(filterModels).includes(prop?.column?.colDef?.field)) ||
        filterColumn
      ) {
        className = globalClasses.visibility;
      }

      return className;
    };

    return (
      <Box
        className={`${classes.responsiveHeaderBox} ${
          props?.column?.colDef?.sortable
            ? globalClasses.cursorPointerClass
            : null
        }`}
      >
        <Box onClick={onClick} className={classes.responsiveHeaderBox}>
          <Typography variant="body1" color={lightPalette.darkPurple.main}>
            {displayName}
            {icon}
          </Typography>
          <Box
            component="img"
            src={filter}
            alt="filter"
            className={filterClassName(filterModel, props, customFilterColumn)}
          />
        </Box>
        {menu}
      </Box>
    );
  },
);

ResponsiveSortingHeader.defaultProps = {
  gridSettingData: null,
  customFilterValue: undefined,
};

ResponsiveSortingHeader.propTypes = {
  displayName: PropTypes.string.isRequired,
  progressSort: PropTypes.func.isRequired,
  showColumnMenu: PropTypes.func.isRequired,
  enableMenu: PropTypes.bool.isRequired,
  api: PropTypes.shape({
    getFilterModel: PropTypes.func.isRequired,
  }).isRequired,
  column: PropTypes.shape({
    colId: PropTypes.string.isRequired,
    isSortAscending: PropTypes.func.isRequired,
    isSortDescending: PropTypes.func.isRequired,
    addEventListener: PropTypes.func.isRequired,
    removeEventListener: PropTypes.func.isRequired,
    colDef: PropTypes.shape({
      sortable: PropTypes.bool,
      field: PropTypes.string,
    }),
  }).isRequired,
  gridSettingData: PropTypes.shape({
    id: PropTypes.string,
    user_id: PropTypes.string,
    grid_key: PropTypes.string,
    settings: PropTypes.shape({
      ' ': PropTypes.bool.isRequired,
      name: PropTypes.bool.isRequired,
      status: PropTypes.bool.isRequired,
      associatedTags: PropTypes.bool.isRequired,
      uploaded: PropTypes.bool.isRequired,
      versions: PropTypes.bool.isRequired,
    }),
    sort: PropTypes.shape({
      column: PropTypes.string,
      sort: PropTypes.string,
    }),
    filter: PropTypes.shape({}),
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }),
  gridKey: PropTypes.string.isRequired,
  customFilterValue: PropTypes.shape({}),
};

export const SelectOption = ({
  totalData,
  selectedRow,
  handleAllTagCompletion,
  gridApi,
  handleApiCompletion,
  rowDataCount,
  allRowSelected,
  searchValue,
  customFilterValue,
  functionData,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const askingMessageData = allRowSelected ? totalData : selectedRow;
  const askingMessage =
    askingMessageData?.length > 1
      ? `Are You Sure You Want to Delete This ${askingMessageData.length} Selected Documents ?`
      : `Are You Sure You Want to Delete This Document : ${askingMessageData[0]?.name} ?`;

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const deleteAllDocument = async (search, filtered, dropDownValue) => {
    try {
      return await deleteAllDocuments(
        search,
        filtered,
        dropDownValue,
        allRowSelected,
        functionData,
      );
    } catch (error) {
      return error;
    }
  };

  const handleConfirmAction = async () => {
    try {
      const selectedRowIds = selectedRow.map((s) => s.id);
      const filterModel = gridApi.getFilterModel();
      const res = allRowSelected
        ? await deleteAllDocument(
            searchValue,
            filterModel,
            customFilterValue,
            allRowSelected,
            functionData,
          )
        : await deleteSelectedDocuments(selectedRowIds, allRowSelected);
      if (res) {
        handleCloseDialog();
        handleApiCompletion();
      }
      if (res.data.message) {
        if (selectedRowIds.length > 1 || allRowSelected) {
          Toast({
            message: DOCUMENT_TOAST.ALL_DOCUMENTS_DELETED,
            variant: 'success',
          });
        } else {
          Toast({
            message: DOCUMENT_TOAST.DOCUMENT_DELETED,
            variant: 'success',
          });
        }
      }
    } catch (error) {
      return error;
    }
  };

  return (
    <Box
      className={`${classes.selectOptionsMainBox} ${
        rowDataCount ? globalClasses.visibility : globalClasses.notVisibility
      }`}
    >
      <Box
        className={`${classes.selectedDocumentBox} ${
          selectedRow.length > 0
            ? globalClasses.visibility
            : globalClasses.notVisibility
        }`}
      >
        <Typography className={classes.documentSelectedTypo}>
          {allRowSelected ? totalData.length : selectedRow.length} Documents
          Selected
        </Typography>
        <OnselectAddTag
          allRowSelected={allRowSelected}
          selectedRow={selectedRow}
          onApiCompletion={handleAllTagCompletion}
          gridApi={gridApi}
          searchValue={searchValue}
          customFilterValue={customFilterValue}
        />
        <ExportDocument
          gridApi={gridApi}
          allRowSelected={allRowSelected}
          searchValue={searchValue}
          customFilterValue={customFilterValue}
        />
        <CustomTooltip title={DOCUMENT_ACTION_TOOLTIP.DELETE}>
          <Box
            component="img"
            src={trash}
            alt="trash"
            onClick={() => handleOpenDialog()}
            className={classes.actionIcons}
          />
        </CustomTooltip>
        {isDialogOpen ? (
          <ConfirmDialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            onConfirm={handleConfirmAction}
            askingMessage={askingMessage}
            isStatusCompleted={undefined}
          />
        ) : null}
      </Box>
    </Box>
  );
};

SelectOption.defaultProps = {
  gridApi: null,
  rowDataCount: null,
};

SelectOption.propTypes = {
  selectedRow: PropTypes.arrayOf(
    PropTypes.shape({
      associatedTags: PropTypes.arrayOf(
        PropTypes.shape({
          created_at: PropTypes.string.isRequired,
          deleted_at: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date),
          ]),
          id: PropTypes.string.isRequired,
          organizations_id: PropTypes.string.isRequired,
          tag: PropTypes.string.isRequired,
          updated_at: PropTypes.string.isRequired,
        }),
      ),
      created_at: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      document_url: PropTypes.string,
      exported: PropTypes.string,
      id: PropTypes.string.isRequired,
      mapped_result: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          mark: PropTypes.shape({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
            type: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired,
          }).isRequired,
          value: PropTypes.string.isRequired,
        }),
      ),
      requested_by: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      updated_at: PropTypes.string.isRequired,
      uploaded: PropTypes.string.isRequired,
      validated: PropTypes.string,
      functionData: PropTypes.string,
    }),
  ).isRequired,
  handleAllTagCompletion: PropTypes.func.isRequired,
  gridApi: PropTypes.shape({
    getSelectedNodes: PropTypes.func.isRequired,
    getFilterModel: PropTypes.func.isRequired,
  }),
  functionData: PropTypes.string.isRequired,
  totalData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      map: PropTypes.func,
    }),
  ).isRequired,
  handleApiCompletion: PropTypes.func.isRequired,
  rowDataCount: PropTypes.number,
  allRowSelected: PropTypes.bool.isRequired,
  searchValue: PropTypes.string.isRequired,
  customFilterValue: PropTypes.shape({}).isRequired,
};
export const VersionSelectOption = ({
  selectedRow,
  allRowSelected,
  gridApi,
  searchValue,
  gridKey,
  handleApiCompletion,
  rowDataCount,
  customFilterValue,
}) => {
  if (gridKey !== 'notifications') {
    return null;
  }

  const handleNotificationButton = async () => {
    const selectedRowIds = selectedRow.map((s) => s.invoice_id);
    const filterModel = gridApi.getFilterModel();
    gridApi?.showLoadingOverlay();
    const response = allRowSelected
      ? await markAllAsReadNotifications(
          searchValue,
          filterModel,
          customFilterValue,
        )
      : await markAsReadForSelectedAsNotifications(selectedRowIds);
    if (response) {
      handleApiCompletion();
    }
  };

  return (
    <Box
      className={`${classes.selectOptionsMainBox} ${
        rowDataCount ? globalClasses.visibility : globalClasses.notVisibility
      }`}
    >
      <Box
        className={`${classes.selectedVersionBox} ${
          selectedRow.length > 0
            ? globalClasses.visibility
            : globalClasses.notVisibility
        }`}
      />
      <Button
        variant="contained"
        className={`${classes.allReadButton} ${
          allRowSelected || selectedRow.length > 0
            ? globalClasses.visibility
            : globalClasses.notVisibility
        }`}
        startIcon={<DoneAllIcon />}
        onClick={handleNotificationButton}
      >
        {selectedRow.length === 1 ? 'Mark as read' : 'Mark all as read'}
      </Button>
    </Box>
  );
};

VersionSelectOption.defaultProps = {
  gridApi: null,
  rowDataCount: null,
};

const UpdaterDeleterPropTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  role_id: PropTypes.number.isRequired,
  admin_id: PropTypes.string,
  country_id: PropTypes.string.isRequired,
  first_name: PropTypes.string.isRequired,
  last_name: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  phone_no: PropTypes.string.isRequired,
  organization_id: PropTypes.string.isRequired,
  avatar: PropTypes.string,
  is_active: PropTypes.bool.isRequired,
  is_verified: PropTypes.bool.isRequired,
  is_registered: PropTypes.bool.isRequired,
  created_at: PropTypes.string.isRequired,
  updated_at: PropTypes.string.isRequired,
  deleted_at: PropTypes.string,
});

const AnnotationDataPropTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  invoice_id: PropTypes.string.isRequired,
  mapped_result: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      mark: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        type: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }).isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  annotation_data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      mark: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        type: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      section: PropTypes.string.isRequired,
      validate: PropTypes.bool,
    }),
  ),
  version: PropTypes.string.isRequired,
  created_at: PropTypes.string.isRequired,
  updated_at: PropTypes.string.isRequired,
  deleted_at: PropTypes.string,
  created_by: PropTypes.string.isRequired,
  updated_by: PropTypes.string.isRequired,
  deleted_by: PropTypes.string,
  organization_id: PropTypes.string.isRequired,
  Creator: UpdaterDeleterPropTypes,
  Updater: UpdaterDeleterPropTypes,
  Deleter: UpdaterDeleterPropTypes,
});

VersionSelectOption.propTypes = {
  selectedRow: PropTypes.arrayOf(AnnotationDataPropTypes).isRequired,
  gridApi: PropTypes.shape({
    getSelectedNodes: PropTypes.func.isRequired,
    getFilterModel: PropTypes.func.isRequired,
    showLoadingOverlay: PropTypes.func.isRequired,
  }),
  handleApiCompletion: PropTypes.func.isRequired,
  rowDataCount: PropTypes.number,
  allRowSelected: PropTypes.bool.isRequired,
  searchValue: PropTypes.string.isRequired,
  gridKey: PropTypes.string.isRequired,
  customFilterValue: PropTypes.shape({}).isRequired,
};

export const CustomLoadingOverlayComponent = () => <CircularProgress />;

let updatedFieldValue = null;

export const CustomDropdownFilter = (prop) => {
  const {
    setCustomFilterValue,
    colDef,
    columnApi,
    gridKey,
    api,
    customFilterValue,
  } = prop;
  const [dropDownList, setDropDownList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dropDownValue, setDropDownValue] = useState([]);
  const [apiCall, setApiCall] = useState(false);
  const loginRole = getLocalStorageItem('role');
  const superAdminOrSuperManager =
    loginRole === ROLES.SUPER_ADMIN || loginRole === ROLES.SUPER_MANAGER;

  const getTags = async () => {
    try {
      const res = await getTagData();
      if (res) {
        const updatedResponse = res.map((r) => ({ ...r, label: r.name }));
        setDropDownList(updatedResponse);
        setDropDownValue(customFilterValue.associatedTags);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      return err.message;
    }
  };

  const status = async () => {
    try {
      const res = await getStage();
      if (res) {
        const updatedResponse = res.map((r) => ({ ...r, label: r.status }));
        const updatedResponseForNotification = res
          .map((r) => ({ ...r, label: r.status }))
          .filter((f) => f.status !== 'Inprogress' && f.status !== 'Pending');
        if (gridKey === 'notifications') {
          setDropDownValue(customFilterValue.document_status);
          setDropDownList(updatedResponseForNotification);
        } else {
          setDropDownValue(customFilterValue.status);
          setDropDownList(updatedResponse);
        }
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      return err.message;
    }
  };

  const countries = async () => {
    try {
      const res = await getCountries();
      if (res) {
        const updatedResponse = res.map((r) => ({
          ...r,
          label: r.country_name,
        }));
        setDropDownList(updatedResponse);
        setDropDownValue(customFilterValue.country_name);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      return err.message;
    }
  };

  useEffect(() => {
    if (colDef?.field === 'associatedTags') {
      getTags();
    } else if (colDef?.field === 'country_name') {
      countries();
    } else if (
      colDef?.field === 'status' ||
      colDef?.field === 'document_status'
    ) {
      status();
    } else if (colDef?.field === 'is_active') {
      const activeStatus = [
        { label: 'Active', value: 'isActive' },
        { label: 'InActive', value: 'isInactive' },
      ];
      setDropDownList(activeStatus);
      setDropDownValue(customFilterValue.is_active);
      setIsLoading(false);
    } else if (colDef?.field === 'notification_status') {
      const readStatus = [
        { label: 'read', value: 'read' },
        { label: 'unread', value: 'unread' },
      ];
      setDropDownList(readStatus);
      setDropDownValue(customFilterValue.notification_status);
      setIsLoading(false);
    } else if (colDef?.field === 'role') {
      const roleStatus = [];
      if (gridKey === 'activity-history') {
        if (superAdminOrSuperManager) {
          roleStatus.push(
            { label: 'Super Admin', value: ROLES.SUPER_ADMIN },
            { label: 'Super Manager', value: ROLES.SUPER_MANAGER },
            { label: 'Admin', value: ROLES.ADMIN },
          );
        }
      } else if (gridKey === 'super-manager') {
        if (superAdminOrSuperManager) {
          roleStatus.push(
            { label: 'Sales Admin', value: ROLES.SALES_ADMIN },
            { label: 'Sales', value: ROLES.SALES },
          );
        } else if (loginRole === ROLES.SALES_ADMIN) {
          roleStatus.push(
            { label: 'Sales Admin', value: ROLES.SALES_ADMIN },
            { label: 'Sales', value: ROLES.SALES },
          );
        } else {
          roleStatus.push({ label: 'Sales', value: ROLES.SALES });
        }
      } else {
        roleStatus.push(
          { label: 'Manager', value: ROLES.MANAGER },
          { label: 'Member', value: ROLES.MEMBER },
        );
        if (superAdminOrSuperManager) {
          roleStatus.push({ label: 'Admin', value: ROLES.ADMIN });
        }
      }

      setDropDownList(roleStatus);
      setDropDownValue(customFilterValue.role);
      setIsLoading(false);
    } else if (colDef?.field === 'document_type') {
      const roleStatus = [
        { label: 'Bank Statement', value: MODELS.BANK_STATEMENT },
        { label: 'Credit Card Statement', value: MODELS.CREDIT_CARD_STATEMENT },
        { label: 'Sales', value: MODELS.SALES },
        { label: 'Direct Expenses', value: MODELS.INVOICE },
        { label: 'Indirect Expenses', value: MODELS.RECEIPT },
      ];
      setDropDownList(roleStatus);
      setDropDownValue(customFilterValue.role);
      setIsLoading(false);
    }
  }, [colDef?.field]);

  const handleAddValue = async (newValue) => {
    setDropDownValue(newValue);
  };

  const handleApplyFilter = async () => {
    setCustomFilterValue((prevDropdownField) => {
      const updatedField = {
        ...prevDropdownField,
        [colDef.field]: dropDownValue,
      };
      updatedFieldValue = updatedField;
      return updatedField;
    });
    setApiCall(true);
    api.hidePopupMenu(false);
  };

  const handleResetFilter = async () => {
    setCustomFilterValue((prevDropdownField) => {
      const updatedField = {
        ...prevDropdownField,
        [colDef.field]: [],
      };
      updatedFieldValue = updatedField;
      return updatedField;
    });
    setDropDownValue([]);
    setApiCall(true);
    api.hidePopupMenu(false);
  };

  const gridUpdate = async (key, sorting, filterValue, dropDownValues) => {
    await gridFilterSortStatus(
      key,
      {
        column: sorting?.colId,
        sort: sorting?.sort,
      },
      filterValue,
      dropDownValues,
    );
  };

  useEffect(() => {
    if (apiCall && updatedFieldValue !== null) {
      const colState = columnApi.getColumnState();
      const filterModel = api.getFilterModel();
      const sortedColumn = colState.find((col) => col.sort !== null);
      gridUpdate(gridKey, sortedColumn, filterModel, updatedFieldValue);
    }
  }, [apiCall, updatedFieldValue]);

  return (
    <Box>
      <Box className={classes.customFilterMainBox}>
        <Autocomplete
          multiple
          value={dropDownValue || []}
          limitTags={2}
          isOptionEqualToValue={(option, newValue) =>
            option.label === newValue.label
          }
          ListboxProps={{ className: 'Autocomplete_scrollbar' }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              const modifiedEvent = { ...event };
              modifiedEvent.defaultMuiPrevented = true;
            }
          }}
          onChange={(event, newValue) => {
            handleAddValue(newValue);
          }}
          selectOnFocus
          clearOnBlur
          PaperComponent={({ children }) => (
            <PaperComponentBox onMouseDown={(event) => event.stopPropagation()}>
              {children}
            </PaperComponentBox>
          )}
          disableCloseOnSelect
          id="free-solo-with-text-demo"
          options={dropDownList || []}
          getOptionLabel={(option) => {
            // Value selected with enter, right from the input
            if (typeof option === 'string') {
              return option;
            }
            // Regular option
            return option.label;
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} />
              <AutoCompleteRenderListTypography variant="body1">
                {option.label}
              </AutoCompleteRenderListTypography>
            </li>
          )}
          // freeSolo
          renderTags={(tagValues, getTagProps) =>
            tagValues.map((option, index) => (
              <Chip
                key={`${index + 1}`}
                label={option.label}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              autoFocus
              {...params}
              placeholder={isLoading ? 'Loading...' : 'Search here'}
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={15} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Box className={classes.customFilterButtonBox}>
          <Button
            variant="outlined"
            onClick={handleResetFilter}
            className={classes.customFilterButtonClass}
          >
            {FILTER.RESET_FILTER}
          </Button>
          <Button
            variant="outlined"
            onClick={handleApplyFilter}
            className={classes.customFilterButtonClass}
          >
            {FILTER.APPLY_FILTER}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
